/**
 * SocialPulseSsoButton.jsx — "Continue with SocialPulse" button.
 *
 * Redesigned Sep 2026 to use the **server-side** OIDC-PKCE flow.
 * On tap, we do a full-page navigation to `/api/auth/socialpulse/init`
 * with the current page as `return_to`. The backend generates the
 * PKCE verifier, stores it server-side, and 302s the browser to the
 * SocialPulse hub's authorize endpoint. After sign-in, the hub redirects
 * to `/auth/socialpulse-callback` (SPA route → `SocialPulseCallbackPage`
 * → forwards to backend → mints wofy_session → redirects to return_to).
 *
 * Everything the browser previously did in-flight (PKCE verifier,
 * sessionStorage state) now lives on the backend. Fewer moving parts,
 * fewer failure modes.
 */
import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";

export default function SocialPulseSsoButton({ next = null, className = "" }) {
  // The button is always visible when the backend env is wired — we
  // don't gate on frontend env vars any more because the frontend no
  // longer needs the CLIENT_ID or ISSUER. A very cheap /api/health
  // probe on mount lets us soft-disable if the origin itself is dead.
  const [originUp, setOriginUp] = useState(true);

  useEffect(() => {
    let alive = true;
    fetch("/api/health", { method: "HEAD", cache: "no-store" })
      .then((r) => { if (alive) setOriginUp(r.ok); })
      .catch(() => { if (alive) setOriginUp(false); });
    return () => { alive = false; };
  }, []);

  const start = () => {
    // return_to is either the caller's `next` prop or the current path.
    const rt = (next && typeof next === "string") ? next
             : (typeof window !== "undefined" ? window.location.pathname + window.location.search : "/");
    const url = `/api/auth/socialpulse/init?return_to=${encodeURIComponent(rt)}`;
    // Full-page navigation (NOT SPA route) — the browser MUST hit the
    // backend so the ingress can route to FastAPI. Using React Router
    // here would trap the URL inside the SPA and never reach FastAPI.
    window.location.href = url;
  };

  if (!originUp) {
    return (
      <div
        className={`inline-flex flex-col items-start gap-1 ${className}`}
        data-testid="socialpulse-sso-unavailable"
        role="status"
      >
        <button
          type="button"
          disabled
          aria-disabled="true"
          className="wofy-btn-ghost text-sm inline-flex items-center gap-2 opacity-55 cursor-not-allowed"
          title="SocialPulse sign-in is temporarily unavailable"
        >
          <span
            aria-hidden
            className="inline-block w-4 h-4 rounded-full"
            style={{ background: "linear-gradient(135deg, #FF6B6B, #FF9B54)", filter: "grayscale(0.5)" }}
          />
          Continue with SocialPulse
        </button>
        <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: "#9a4a1c" }} data-testid="socialpulse-sso-hint">
          <AlertTriangle className="w-3 h-3" aria-hidden />
          SocialPulse temporarily unavailable — try Google or email.
        </span>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={start}
      className={`wofy-btn-ghost text-sm inline-flex items-center gap-2 ${className}`}
      data-testid="socialpulse-sso-button"
    >
      <img
        src="https://socialpulse.com/assets/social-pulse-logo.jpg"
        alt=""
        aria-hidden="true"
        className="w-4 h-4 rounded"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
      />
      Continue with SocialPulse
    </button>
  );
}
